import React, { useState } from 'react';
import styled from 'styled-components';
import { Users, Plus, Edit, Trash2, Search, History, Award, Phone, Mail, Calendar } from 'lucide-react';
import Sidebar from '../components/layout/Sidebar';
import Footer from '../components/layout/Footer';
import { Button } from '../components/common/Button';
import { Card } from '../components/common/Card';
import { Input, InputGroup } from '../components/common/Input';
import { theme } from '../styles/theme';

const Container = styled.div`
  display: flex;
  min-height: 100vh;
  background-color: ${theme.colors.background};
`;

const MainContent = styled.main`
  flex: 1;
  margin-left: 260px;
  padding: ${theme.spacing.xl};
  padding-bottom: 80px;

  @media (max-width: ${theme.breakpoints.lg}) {
    margin-left: 0;
    padding: ${theme.spacing.md};
    padding-top: 70px;
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    padding: ${theme.spacing.sm};
    padding-top: 70px;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing.xl};
  flex-wrap: wrap;
  gap: ${theme.spacing.md};
  
  h1 {
    font-size: ${theme.typography.fontSize['3xl']};
    font-weight: ${theme.typography.fontWeight.bold};
    color: ${theme.colors.text};
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    h1 {
      font-size: ${theme.typography.fontSize['2xl']};
    }
  }
`;

const Tabs = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
  margin-bottom: ${theme.spacing.lg};
  border-bottom: 2px solid ${theme.colors.border};
`;

const Tab = styled.button`
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  border: none;
  background: transparent;
  border-bottom: 3px solid ${({ active }) => active ? theme.colors.primary : 'transparent'};
  color: ${({ active }) => active ? theme.colors.primary : theme.colors.textSecondary};
  font-weight: ${({ active }) => active ? theme.typography.fontWeight.semiBold : theme.typography.fontWeight.regular};
  font-family: ${theme.typography.fontFamily};
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: ${theme.typography.fontSize.base};
`;

const ActionsBar = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.lg};
  padding: ${theme.spacing.lg};
  background-color: ${theme.colors.white};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.md};
  flex-wrap: wrap;
`;

const CustomersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: ${theme.spacing.lg};

  @media (max-width: ${theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const CustomerCard = styled(Card)`
  padding: ${theme.spacing.lg};
  position: relative;
`;

const CustomerHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${theme.spacing.md};
`;

const CustomerInfo = styled.div`
  flex: 1;

  h3 {
    font-size: ${theme.typography.fontSize.xl};
    font-weight: ${theme.typography.fontWeight.bold};
    margin-bottom: ${theme.spacing.xs};
    color: ${theme.colors.text};
  }

  p {
    font-size: ${theme.typography.fontSize.sm};
    color: ${theme.colors.textSecondary};
    margin-bottom: ${theme.spacing.xs};
    display: flex;
    align-items: center;
    gap: ${theme.spacing.xs};
  }
`;

const LoyaltyBadge = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.full};
  background-color: ${theme.colors.accent}20;
  color: ${theme.colors.accent};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.semiBold};
`;

const StatsRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: ${theme.spacing.md};
  background-color: ${theme.colors.secondary};
  border-radius: ${theme.borderRadius.md};
  margin-bottom: ${theme.spacing.md};
`;

const StatItem = styled.div`
  text-align: center;

  h4 {
    font-size: ${theme.typography.fontSize['2xl']};
    font-weight: ${theme.typography.fontWeight.bold};
    color: ${theme.colors.primary};
    margin-bottom: ${theme.spacing.xs};
  }

  p {
    font-size: ${theme.typography.fontSize.xs};
    color: ${theme.colors.textSecondary};
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
  flex-wrap: wrap;
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: ${theme.zIndex.modal};
  padding: ${theme.spacing.lg};
`;

const ModalContent = styled(Card)`
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing.lg};
  padding-bottom: ${theme.spacing.md};
  border-bottom: 2px solid ${theme.colors.border};

  h2 {
    font-size: ${theme.typography.fontSize.xl};
    font-weight: ${theme.typography.fontWeight.semiBold};
    color: ${theme.colors.text};
  }
`;

const CloseButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: ${theme.borderRadius.md};
  border: none;
  background-color: ${theme.colors.secondary};
  color: ${theme.colors.text};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${theme.colors.status.outOfStock};
    color: ${theme.colors.white};
  }
`;

const ModalForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.lg};
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  justify-content: flex-end;
  margin-top: ${theme.spacing.lg};
`;

const HistoryTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: ${theme.spacing.lg};

  th, td {
    padding: ${theme.spacing.md};
    text-align: left;
    border-bottom: 1px solid ${theme.colors.border};
  }

  th {
    background-color: ${theme.colors.secondary};
    font-weight: ${theme.typography.fontWeight.semiBold};
    font-size: ${theme.typography.fontSize.sm};
  }
`;

// Mock data
const initialCustomers = [
  {
    id: 1,
    name: 'John Doe',
    email: 'john@example.com',
    phone: '+91 9876543210',
    joinDate: '2024-01-15',
    totalOrders: 45,
    totalSpent: 12500,
    loyaltyPoints: 1250,
    lastVisit: '2024-12-20',
  },
  {
    id: 2,
    name: 'Jane Smith',
    email: 'jane@example.com',
    phone: '+91 9876543211',
    joinDate: '2024-03-20',
    totalOrders: 32,
    totalSpent: 8900,
    loyaltyPoints: 890,
    lastVisit: '2024-12-18',
  },
];

function CustomerManagement() {
  const [activeTab, setActiveTab] = useState('customers');
  const [customers, setCustomers] = useState(initialCustomers);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    joinDate: new Date().toISOString().split('T')[0],
  });

  const handleAddCustomer = () => {
    setEditingCustomer(null);
    setFormData({
      name: '',
      email: '',
      phone: '',
      joinDate: new Date().toISOString().split('T')[0],
    });
    setShowModal(true);
  };

  const handleEdit = (customer) => {
    setEditingCustomer(customer);
    setFormData({
      name: customer.name,
      email: customer.email,
      phone: customer.phone,
      joinDate: customer.joinDate,
    });
    setShowModal(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (editingCustomer) {
      setCustomers((prev) =>
        prev.map((customer) =>
          customer.id === editingCustomer.id
            ? { ...customer, ...formData }
            : customer
        )
      );
    } else {
      const newCustomer = {
        id: Date.now(),
        ...formData,
        totalOrders: 0,
        totalSpent: 0,
        loyaltyPoints: 0,
        lastVisit: null,
      };
      setCustomers((prev) => [...prev, newCustomer]);
    }
    setShowModal(false);
  };

  const filteredCustomers = customers.filter((customer) =>
    customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.phone.includes(searchTerm)
  );

  return (
    <Container>
      <Sidebar />
      <MainContent>
        <Header>
          <h1>Customer Management</h1>
          <Button variant="primary" onClick={handleAddCustomer}>
            <Plus size={20} />
            Add Customer
          </Button>
        </Header>

        <Tabs>
          <Tab active={activeTab === 'customers'} onClick={() => setActiveTab('customers')}>
            Customers
          </Tab>
          <Tab active={activeTab === 'loyalty'} onClick={() => setActiveTab('loyalty')}>
            Loyalty Program
          </Tab>
        </Tabs>

        {activeTab === 'customers' && (
          <>
            <ActionsBar>
              <Input
                type="text"
                placeholder="Search customers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ flex: 1, maxWidth: '400px' }}
              />
            </ActionsBar>

            <CustomersGrid>
              {filteredCustomers.map((customer) => (
                <CustomerCard key={customer.id}>
                  <CustomerHeader>
                    <CustomerInfo>
                      <h3>{customer.name}</h3>
                      <p>
                        <Mail size={14} />
                        {customer.email}
                      </p>
                      <p>
                        <Phone size={14} />
                        {customer.phone}
                      </p>
                      <p>
                        <Calendar size={14} />
                        Joined: {new Date(customer.joinDate).toLocaleDateString()}
                      </p>
                    </CustomerInfo>
                  </CustomerHeader>

                  <StatsRow>
                    <StatItem>
                      <h4>{customer.totalOrders}</h4>
                      <p>Orders</p>
                    </StatItem>
                    <StatItem>
                      <h4>₹{customer.totalSpent}</h4>
                      <p>Total Spent</p>
                    </StatItem>
                    <StatItem>
                      <h4>{customer.loyaltyPoints}</h4>
                      <p>Points</p>
                    </StatItem>
                  </StatsRow>

                  <LoyaltyBadge>
                    <Award size={16} />
                    {customer.loyaltyPoints >= 1000 ? 'Gold Member' : customer.loyaltyPoints >= 500 ? 'Silver Member' : 'Bronze Member'}
                  </LoyaltyBadge>

                  <ActionButtons style={{ marginTop: theme.spacing.md }}>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSelectedCustomer(customer);
                        setActiveTab('history');
                      }}
                    >
                      <History size={16} />
                      View History
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(customer)}
                    >
                      <Edit size={16} />
                      Edit
                    </Button>
                  </ActionButtons>
                </CustomerCard>
              ))}
            </CustomersGrid>
          </>
        )}

        {activeTab === 'loyalty' && (
          <Card>
            <h2 style={{ marginBottom: theme.spacing.lg }}>Loyalty Program Settings</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.md }}>
              <div>
                <h3>Points System</h3>
                <p>1 point = ₹10 spent</p>
                <p>100 points = ₹50 discount</p>
              </div>
              <div>
                <h3>Tiers</h3>
                <ul>
                  <li>Bronze: 0-499 points</li>
                  <li>Silver: 500-999 points (5% discount)</li>
                  <li>Gold: 1000+ points (10% discount)</li>
                </ul>
              </div>
            </div>
          </Card>
        )}

        {activeTab === 'history' && selectedCustomer && (
          <Card>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: theme.spacing.lg }}>
              <h2>Order History - {selectedCustomer.name}</h2>
              <Button variant="secondary" size="sm" onClick={() => setActiveTab('customers')}>
                Back
              </Button>
            </div>
            <HistoryTable>
              <thead>
                <tr>
                  <th>Order ID</th>
                  <th>Date</th>
                  <th>Items</th>
                  <th>Amount</th>
                  <th>Points Earned</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>ORD-001</td>
                  <td>2024-12-20</td>
                  <td>Paneer Tikka, Biryani</td>
                  <td>₹600</td>
                  <td>60</td>
                </tr>
                <tr>
                  <td>ORD-002</td>
                  <td>2024-12-18</td>
                  <td>Butter Naan, Dal</td>
                  <td>₹250</td>
                  <td>25</td>
                </tr>
              </tbody>
            </HistoryTable>
          </Card>
        )}

        {showModal && (
          <Modal onClick={() => setShowModal(false)}>
            <ModalContent onClick={(e) => e.stopPropagation()}>
              <ModalHeader>
                <h2>{editingCustomer ? 'Edit Customer' : 'Add Customer'}</h2>
                <CloseButton onClick={() => setShowModal(false)}>×</CloseButton>
              </ModalHeader>
              <ModalForm onSubmit={handleSubmit}>
                <InputGroup>
                  <label>Full Name</label>
                  <Input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </InputGroup>
                <InputGroup>
                  <label>Email</label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                  />
                </InputGroup>
                <InputGroup>
                  <label>Phone</label>
                  <Input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    required
                  />
                </InputGroup>
                <InputGroup>
                  <label>Join Date</label>
                  <Input
                    type="date"
                    value={formData.joinDate}
                    onChange={(e) => setFormData({ ...formData, joinDate: e.target.value })}
                    required
                  />
                </InputGroup>
                <ButtonGroup>
                  <Button variant="secondary" type="button" onClick={() => setShowModal(false)}>
                    Cancel
                  </Button>
                  <Button variant="primary" type="submit">
                    {editingCustomer ? 'Update' : 'Add'} Customer
                  </Button>
                </ButtonGroup>
              </ModalForm>
            </ModalContent>
          </Modal>
        )}
      </MainContent>
      <Footer />
    </Container>
  );
}

export default CustomerManagement;

